import { useState, useRef, useCallback } from 'react';
import { Key } from 'lucide-react';

interface MobileCardField {
  name: string;
  value: unknown;
  type?: string;
  isPK?: boolean;
}

interface MobileCardProps {
  fields: MobileCardField[];
  onTap?: () => void;
  onLongPress?: (e: React.TouchEvent) => void;
  /** Max fields visible before "Show all" toggle. Defaults to 6. */
  maxVisible?: number;
}

function formatValue(value: unknown, type?: string): { text: string; className: string } {
  if (value === null || value === undefined) {
    return { text: 'NULL', className: 'italic text-text-muted' };
  }
  if (typeof value === 'boolean') {
    return { text: String(value), className: 'text-syn-keyword' };
  }
  if (typeof value === 'number') {
    return { text: String(value), className: 'text-syn-number' };
  }
  if (type && (type.includes('int') || type.includes('float') || type.includes('numeric') || type.includes('decimal') || type.includes('double'))) {
    return { text: String(value), className: 'text-syn-number' };
  }
  if (type === 'boolean' || type === 'bool') {
    return { text: String(value), className: 'text-syn-keyword' };
  }
  const str = typeof value === 'object' ? JSON.stringify(value) : String(value);
  return { text: str, className: 'text-text-primary' };
}

export function MobileCard({ fields, onTap, onLongPress, maxVisible = 6 }: MobileCardProps) {
  const [expanded, setExpanded] = useState(false);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const didLongPress = useRef(false);
  const startX = useRef(0);
  const startY = useRef(0);

  const cancelLongPress = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      didLongPress.current = false;
      const touch = e.touches[0];
      startX.current = touch.clientX;
      startY.current = touch.clientY;
      if (onLongPress) {
        longPressTimer.current = setTimeout(() => {
          didLongPress.current = true;
          onLongPress(e);
        }, 500);
      }
    },
    [onLongPress],
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      const touch = e.touches[0];
      if (
        Math.abs(touch.clientX - startX.current) > 10 ||
        Math.abs(touch.clientY - startY.current) > 10
      ) {
        cancelLongPress();
      }
    },
    [cancelLongPress],
  );

  const handleTouchEnd = useCallback(() => {
    cancelLongPress();
  }, [cancelLongPress]);

  const handleClick = useCallback(() => {
    if (!didLongPress.current && onTap) {
      onTap();
    }
  }, [onTap]);

  const visibleFields = expanded ? fields : fields.slice(0, maxVisible);
  const hasMore = fields.length > maxVisible;

  return (
    <div
      className="rounded-lg border px-3 py-2.5 active:bg-dbgo-tab-hover transition-colors"
      style={{
        backgroundColor: 'var(--dbgo-panel)',
        borderColor: 'var(--border-default)',
      }}
      onClick={handleClick}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
    >
      {visibleFields.map((field) => {
        const { text, className } = formatValue(field.value, field.type);
        return (
          <div key={field.name} className="flex items-baseline gap-2 py-0.5">
            <span className="text-xs text-text-secondary flex items-center gap-1 flex-shrink-0">
              {field.isPK && <Key size={10} className="text-accent-yellow" />}
              {field.name}
            </span>
            <span className={`text-sm truncate min-w-0 ${className}`}>{text}</span>
          </div>
        );
      })}
      {hasMore && !expanded && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(true);
          }}
          className="text-xs text-accent-blue mt-1"
        >
          Show all {fields.length} fields
        </button>
      )}
      {hasMore && expanded && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(false);
          }}
          className="text-xs text-accent-blue mt-1"
        >
          Show less
        </button>
      )}
    </div>
  );
}
